import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "../App.css";
import useFocusTrap from "../hooks/useFocusTrap";

const CARD_BG_ASPECT = 16 / 9;

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

async function getCroppedBackground(imageSrc, cropPixels, outputWidth = 1920, outputHeight = 1080) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas context unavailable");
  }

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        const file = new File([blob], `card-bg-${Date.now()}.webp`, { type: "image/webp" });
        const previewUrl = URL.createObjectURL(blob);
        resolve({ file, previewUrl });
      },
      "image/webp",
      0.9
    );
  });
}

export default function CardBackgroundCropperModal({ src, onApply, onClose }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const previewBoxRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useFocusTrap({
    isOpen: true,
    containerRef: modalRef,
    onClose,
    initialFocusRef: closeButtonRef,
    lockScroll: true,
  });

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  useEffect(() => {
    const el = previewBoxRef.current;
    if (!el) return undefined;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      setPreviewSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    update();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } else {
      window.addEventListener("resize", update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const drawPreview = async () => {
      if (!croppedAreaPixels || !previewCanvasRef.current) return;
      const { width, height } = previewSize;
      if (!width || !height) return;
      try {
        const image = await createImage(src);
        if (cancelled) return;
        const canvas = previewCanvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          width,
          height
        );
      } catch (_err) {
        // ignore preview failures
      }
    };
    drawPreview();
    return () => {
      cancelled = true;
    };
  }, [croppedAreaPixels, previewSize, src]);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const result = await getCroppedBackground(src, croppedAreaPixels);
      onApply(result);
    } catch (_err) {
      // ignore crop failures
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 255 }}>
      <div
        ref={modalRef}
        className="modal cropping-modal card-bg-cropping-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="card-bg-cropper-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="card-bg-cropper-modal-title">Crop Card Background</h3>
          <button ref={closeButtonRef} className="icon-btn" type="button" onClick={onClose} aria-label="Close background editor">
            x
          </button>
        </div>
        <div className="card-bg-cropper-layout">
          <div className="cropper-container card-bg-cropper-container">
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={CARD_BG_ASPECT}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <aside className="card-bg-preview-panel" aria-hidden="true">
            <p className="muted small" style={{ margin: 0 }}>
              This matches the card header area.
            </p>
            <div className="card-bg-preview-box" ref={previewBoxRef}>
              {croppedAreaPixels ? (
                <canvas ref={previewCanvasRef} />
              ) : (
                <img src={src} alt="" />
              )}
              <div className="card-bg-preview-avatar" />
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              Ratio: 16:9
            </p>
          </aside>
        </div>
        <div className="cropper-controls">
          <label htmlFor="card-background-cropper-zoom">Zoom</label>
          <input
            id="card-background-cropper-zoom"
            type="range"
            min={1}
            max={3}
            step={0.02}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>
        <div className="button-row" style={{ justifyContent: "flex-end" }}>
          <button className="ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary dark" type="button" onClick={handleApply} disabled={saving}>
            {saving ? "Applying..." : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
